import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Trash2 } from 'lucide-react';
import { DangerButton, DefaultButton } from '../Common/BaseButton';
import { deleteTimeEntry } from '../../services/supabase';


const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #dc2626;
`;

const ModalText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #666;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;



const DeleteTimeEntryButton = ({ timeEntry, currentUserId, onDelete }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (showConfirm) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showConfirm]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await deleteTimeEntry(timeEntry.id, currentUserId);
      
      if (error) {
        throw error;
      }
      
      onDelete(timeEntry.id);
      setShowConfirm(false);
    } catch (err) {
      console.error('Error deleting time entry:', err);
      alert('Failed to delete time entry. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DangerButton
        onClick={() => setShowConfirm(true)}
        title="Delete time entry"
        disabled={deleting}
      >
        <Trash2 size={14} />
      </DangerButton>

      {showConfirm && createPortal(
        <ConfirmModal onClick={() => setShowConfirm(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Delete Time Entry</ModalTitle>
            <ModalText>
              Are you sure you want to delete this time entry? This action cannot be undone.
            </ModalText>
            <ModalButtons>
              <DefaultButton onClick={() => setShowConfirm(false)}>
                Cancel
              </DefaultButton>
              <DangerButton 
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </DangerButton>
            </ModalButtons>
          </ModalContent>
        </ConfirmModal>,
        document.body
      )}
    </>
  );
};

export default DeleteTimeEntryButton;